'use client'

import React from 'react'
import { ButtonLink } from '@/components/Buttons/Buttons'
import { BrandWordmark } from '@/components/ui/BrandWordmark'
import { usePathname, useRouter } from 'next/navigation'

export type BuyDriveButtonProps = {
  href?: string
  size?: 'sm' | 'md' | 'lg'
  className?: string
  /** Text before the D.R.I.V.E. logo */
  prefixText?: string
  /** Text after the D.R.I.V.E. logo; leave undefined to show default, or set to empty string to hide */
  suffixText?: string
  /** Optional course slug to direct checkout on pricing; defaults to drive-mastery */
  courseSlug?: string
}

/**
 * BuyDriveButton – matches the header BUY D.R.I.V.E NOW button style
 * Usage:
 *   <BuyDriveButton />
 *   <BuyDriveButton size="md" className="mx-auto" />
 */
export function BuyDriveButton({
  href,
  size = 'sm',
  className,
  prefixText = 'BUY',
  suffixText = 'NOW',
  courseSlug,
}: BuyDriveButtonProps) {
  const router = useRouter()
  const pathname = usePathname()
  const resolvedHref =
    href || `/pricing?buy=1&course=${encodeURIComponent(courseSlug || 'drive-mastery')}`
  return (
    <ButtonLink
      size={size}
      color="light"
      href={resolvedHref}
      onClick={(e?: React.MouseEvent) => {
        try {
          if (typeof window !== 'undefined' && pathname === '/pricing') {
            if (typeof window.openPurchaseModal === 'function') {
              e?.preventDefault()
              window.openPurchaseModal()
              return
            }
            // Fallback: force a router push with a cache-busting param to trigger useSearchParams effect
            e?.preventDefault()
            const url = new URL(window.location.href)
            url.searchParams.set('buy', '1')
            url.searchParams.set('r', String(Date.now()))
            router.push(url.pathname + '?' + url.searchParams.toString())
            return
          }
        } catch {}
      }}
      className={
        // Matches header: rounded-md, gray bg, subtle border
        `rounded-md bg-[#5A5A5A] hover:bg-[#6A6A6A] text-foreground border border-white/20 ${className || ''}`
      }
    >
      <span className="whitespace-nowrap inline-flex items-center">
        <span>{prefixText}</span>
        <BrandWordmark fallbackHtml={null} className="mx-1 -translate-y-px" />
        {typeof suffixText === 'string' && suffixText.length > 0 ? (
          <span className="ml-1">{suffixText}</span>
        ) : null}
      </span>
    </ButtonLink>
  )
}
